/**
 * ResearchSection.jsx — chooses the new Research workspace or the legacy tabs.
 *
 * The new four-tab layout is the default; the seven legacy tabs stay reachable
 * with ?legacy=1 and are NOT removed (HANDOFF §5.1, §6 Phase 5). The switch
 * lives here so exactly one place decides, and both mount points — ArenaShell
 * and the console preview — get the same behaviour.
 */

import React, { useEffect, useState } from "react";

import ResearcherPage from "../arena/ResearcherPage";
import ResearchWorkspace from "./ResearchWorkspace";
import ResearchDesk from "./desk/ResearchDesk";
import ResearchFeed from "./desk/ResearchFeed";
import ResearchCoverage from "./desk/ResearchCoverage";
import BriefArchive from "./brief/BriefArchive";
import BriefIssuePage from "./brief/BriefIssue";
import BriefAdmin from "./brief/BriefAdmin";
import ResearchHome from "./home/ResearchHome";
import ResearchStory from "./home/ResearchStory";
import ResearchCoin from "./home/ResearchCoin";
import { useResearchLocation } from "./desk/researchNavigation";
import { useResearchTheme } from './ui/useResearchTheme';
import { ResearchNav } from './ui/ResearchUI';

const readLegacy = () => {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("legacy") === "1";
  } catch {
    return false;
  }
};

export default function ResearchSection() {
  const [legacy, setLegacy] = useState(readLegacy);
  const path = useResearchLocation();
  const [theme, changeTheme] = useResearchTheme();

  // The flag lives in the query string while the tab lives in the hash, so a
  // tab change must not silently drop the operator back to the new UI.
  useEffect(() => {
    const sync = () => setLegacy(readLegacy());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  if (legacy) {
    return (
      <div>
        <div style={{
          border: "1px solid #242A26", borderRadius: 12, padding: "10px 14px",
          marginBottom: 12, fontSize: 12, color: "#9AA39B",
          background: "#161A17", fontFamily: "IBM Plex Sans, system-ui, sans-serif",
        }}>
          Legacy Research tabs.{" "}
          <a href={typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.hash}`
            : "#research/overview"}
             style={{ color: "#C9F24A" }}>
            Switch to the new Research →
          </a>
        </div>
        <ResearcherPage />
      </div>
    );
  }
  if (path === "/research/feed") return <ResearchFeed />;
  if (path === "/research/coverage") return <ResearchCoverage />;
  if (path === "/research/brief") return <div className="research-desk research-brief" data-theme={theme}><ResearchNav theme={theme} onThemeChange={changeTheme}/><BriefArchive /></div>;
  if (path === "/research/brief/admin") return <div className="research-desk research-brief" data-theme={theme}><ResearchNav theme={theme} onThemeChange={changeTheme}/><BriefAdmin /></div>;
  if (path.startsWith("/research/brief/")) return <div className="research-desk research-brief" data-theme={theme}><ResearchNav theme={theme} onThemeChange={changeTheme}/><BriefIssuePage /></div>;
  if (path === "/research") return <ResearchHome />;
  if (path.startsWith("/research/story/")) return <ResearchStory />;
  if (path.startsWith("/research/coins/")) return <ResearchCoin />;
  if (path === "/research/lab" || (path === "/" && window.location.hash.startsWith("#research/"))) {
    return <div className="research-desk research-lab" data-theme={theme}><ResearchNav theme={theme} onThemeChange={changeTheme}/><ResearchWorkspace /></div>;
  }
  return <ResearchDesk />;
}
